import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import AiSummaryIframe from './AiSummaryIframe';

interface ProjectRefreshIframeProps {
  data: {
    projectId: string;
    summary: string;
    latestCharCount: number;
    charDiff: number;
    usedApproximateOldRevision?: boolean;
    comparisionId?: string; 
  } | null;
  onClose: () => void;
  showSummaryType?: boolean; 
  userId: string;
}

const ProjectRefreshIframe: React.FC<ProjectRefreshIframeProps> = ({ data, onClose, showSummaryType: showSummaryTypeProp, userId }) => {
  const [showSummaryType, setShowSummaryType] = useState(!!showSummaryTypeProp);

  // Sync prop to state
  useEffect(() => {
    if (showSummaryTypeProp) setShowSummaryType(true);
  }, [showSummaryTypeProp]);

  if (!data || (data as any).__loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full flex flex-col items-center">
          <div className="mb-4 animate-spin">
            {/* Simple CSS spinner */}
            <svg className="h-10 w-10 text-custom-blue-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
          <div className="text-lg font-semibold text-custom-blue-900 mb-2">Refreshing a project...</div>
          <div className="text-sm text-gray-500">This may take a few seconds.</div>
        </div>
      </div>
    );
  }
  // If only summary exists (e.g., for uptodate message), show only that
  if (data.summary && !data.projectId && !data.latestCharCount && !data.charDiff) {
    const isUpToDate = data.summary.trim().toLowerCase().includes('already up to date');
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative flex flex-col items-center">
          <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-black">&times;</button>
          <h2 className="text-lg font-bold mb-4">Project Refresh Result</h2>
          <div className="text-center text-custom-blue-900 text-base font-semibold py-8">{data.summary}</div>
          {!isUpToDate && (
            <Button
              className="mt-4 w-40 mx-auto bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                const revisionId = (data as any).revisionId;
                if (revisionId) {
                  window.location.href = `/pending-posts?revisionId=${revisionId}`;
                } else {
                  window.location.href = '/pending-posts';
                }
              }}
            >
              Edit This Post
            </Button>
          )}
          <Button className="mt-4 w-40 mx-auto bg-custom-blue-600 hover:bg-custom-blue-700 text-white" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }
  if (showSummaryType) {
    console.log('ProjectRefreshIframe showSummaryType data:', data); // Debug log
    const comparisionId = data?.comparisionId;

    if (!comparisionId || comparisionId.trim() === '') {
      console.error('comparisionId is missing or empty:', comparisionId);
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative flex flex-col items-center">
            <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-black">&times;</button>
            <h2 className="text-lg font-bold mb-4 text-red-600">Error</h2>
            <div className="text-center text-gray-700 mb-4">
              Unable to generate AI summary. Revision comparison ID is missing.
              <br />
              Please try refreshing the project again.
            </div>
            <Button className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white" onClick={onClose}>Close</Button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative flex flex-col items-center">
          <AiSummaryIframe
            onCancel={() => { setShowSummaryType(false); setTimeout(onClose, 0); }} // Close summary modal, then parent (defer)
            onConfirm={() => { setShowSummaryType(false); setTimeout(onClose, 0); }} // Close summary modal, then parent (defer)
            comparisionId={comparisionId}
            userId={userId}
            onCloseAll={() => {
              setShowSummaryType(false); // Always close summary modal
              setTimeout(onClose, 0); // Call parent onClose (refresh projects, close modal) after state update
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative flex flex-col items-center">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-black">&times;</button>
        <h2 className="text-lg font-bold mb-4">Project Refresh Result</h2>
        {/* Warning message: only show if usedApproximateOldRevision is true */}
        {data.usedApproximateOldRevision && (
          <div className="w-full mb-4 flex justify-center">
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded text-sm text-center">
              ⚠️ Myth Dimension can only track the <b>Master Revision</b> of your Google Docs.<br />
              Branches and detailed version history may not be reflected.
            </div>
          </div>
        )}
        {/* Character stats in gray box */}
        <div className="w-full bg-gray-100 border border-gray-300 rounded p-4 mb-2 flex flex-col items-center">
          <div className="mb-1 w-full text-center"><span className="font-semibold">Character Count Change:</span> {data.charDiff >= 0 ? `+${data.charDiff}` : data.charDiff}</div>
          <div className="w-full text-center"><span className="font-semibold">Total Character Count:</span> {data.latestCharCount}</div>
        </div>
        <div className="flex w-full mt-2 gap-4 flex-row-reverse">
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => setShowSummaryType(true)}
          >
            Create Summary
          </Button>
          <Button className="flex-1 bg-custom-blue-600 hover:bg-custom-blue-700 text-white" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectRefreshIframe;
